import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { UserPlus, X, Tag, Handshake, DollarSign, Lock, ChevronRight } from "lucide-react";
import { computeStats, getWeekKey, getMonthKey } from "@/lib/chartUtils";
import { addMember as addMemberApi, removeMember as removeMemberApi, verifyAdminPin } from "@/lib/api";

export default function RosterView({ members: rawMembers, events, onDataChanged }) {
  const members = [...rawMembers].sort((a, b) => a.localeCompare(b));
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState(false);

  const wk = getWeekKey(new Date());
  const mk = getMonthKey(new Date());
  const wEvs = events.filter(e => e.weekKey === wk);
  const mEvs = events.filter(e => e.monthKey === mk);
  const wStats = computeStats(wEvs);
  const mStats = computeStats(mEvs);

  // Check cached admin PIN on mount
  useEffect(() => {
    const cached = localStorage.getItem("chart_admin_pin");
    if (cached) {
      verifyAdminPin(cached).then(valid => {
        if (valid) setIsAdmin(true);
        else localStorage.removeItem("chart_admin_pin");
      });
    }
  }, []);

  async function handlePinSubmit() {
    const valid = await verifyAdminPin(pinInput);
    if (valid) {
      setIsAdmin(true);
      localStorage.setItem("chart_admin_pin", pinInput);
      setShowPin(false);
      setPinError(false);
      setPinInput("");
    } else {
      setPinError(true);
    }
  }

  async function handleAdd() {
    const n = newName.trim().replace(/^\w/, c => c.toUpperCase());
    if (!n) return;
    try {
      setLoading(true);
      const pin = localStorage.getItem("chart_admin_pin") || "";
      await addMemberApi(n, pin);
      setNewName("");
      onDataChanged();
    } catch {
      // member might already exist
    } finally {
      setLoading(false);
    }
  }

  async function handleRemove(name) {
    if (!window.confirm(`Remove ${name} from the roster?`)) return;
    try {
      const pin = localStorage.getItem("chart_admin_pin") || "";
      await removeMemberApi(name, pin);
      onDataChanged();
    } catch {
      // handled silently
    }
  }

  const colors = [
    "bg-orange-600", "bg-blue-600", "bg-purple-600", "bg-green-600",
    "bg-red-600", "bg-teal-600", "bg-indigo-600", "bg-pink-600", "bg-amber-600"
  ];

  return (
    <div data-testid="roster-view">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[10px] font-bold text-zinc-400 tracking-[0.2em] uppercase">
          Members ({members.length})
        </h3>
        {!isAdmin ? (
          <button
            data-testid="roster-admin-btn"
            onClick={() => setShowPin(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-md"
          >
            <Lock className="w-3.5 h-3.5" /> Admin
          </button>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 rounded-md border border-green-200">
            <Lock className="w-3.5 h-3.5" /> Admin Mode
          </span>
        )}
      </div>

      {/* PIN Modal */}
      {showPin && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div data-testid="roster-pin-modal" className="bg-white rounded-lg p-6 w-full max-w-xs shadow-xl">
            <h3 className="text-sm font-bold text-zinc-950 mb-1">Admin Access</h3>
            <p className="text-xs text-zinc-500 mb-4">Enter PIN to manage roster.</p>
            <input
              data-testid="roster-pin-input"
              type="password"
              inputMode="numeric"
              value={pinInput}
              onChange={e => { setPinInput(e.target.value); setPinError(false); }}
              onKeyDown={e => e.key === "Enter" && handlePinSubmit()}
              placeholder="PIN"
              className={`w-full px-3 py-2.5 text-center text-lg font-bold tracking-[0.3em] border-2 rounded-md outline-none ${
                pinError ? "border-red-400 bg-red-50" : "border-zinc-200 focus:border-orange-500"
              }`}
              autoFocus
            />
            {pinError && <p className="text-xs text-red-600 mt-1.5 text-center">Incorrect PIN</p>}
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setShowPin(false); setPinInput(""); setPinError(false); }} className="flex-1 px-3 py-2 text-xs font-bold text-zinc-600 border border-zinc-200 rounded-md">Cancel</button>
              <button data-testid="roster-pin-submit" onClick={handlePinSubmit} className="flex-1 px-3 py-2 text-xs font-bold text-white bg-orange-600 hover:bg-orange-700 rounded-md">Unlock</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-6">
        {members.map((m, i) => {
          const ws = wStats[m] || { tags: 0, referrals: 0 };
          const ms = mStats[m] || { closedRx: 0 };
          return (
            <Link
              key={m}
              to={`/member/${encodeURIComponent(m)}`}
              data-testid={`roster-member-${m}`}
              className="bg-white border border-zinc-100 rounded-lg p-4 text-center relative group hover:border-orange-200 hover:shadow-sm transition-all block"
            >
              {isAdmin && (
                <button
                  data-testid={`remove-member-${m}`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemove(m); }}
                  className="absolute top-2 right-2 p-1 rounded-md bg-red-50 border border-red-200 sm:opacity-0 sm:group-hover:opacity-100 transition-all z-10"
                  title="Remove"
                >
                  <X className="w-3 h-3 text-red-500" />
                </button>
              )}
              <div className={`w-11 h-11 rounded-full ${colors[i % colors.length]} text-white font-extrabold text-lg flex items-center justify-center mx-auto mb-2`}>
                {m[0]}
              </div>
              <p className="text-sm font-bold text-zinc-950 mb-2">{m}</p>
              <div className="space-y-0.5 text-[11px] text-zinc-500">
                <div className="flex items-center justify-center gap-1">
                  <Tag className="w-3 h-3" /> Wk Tags: {ws.tags || 0}
                </div>
                <div className="flex items-center justify-center gap-1">
                  <Handshake className="w-3 h-3" /> Wk Refs: {ws.referrals || 0}
                </div>
                <div className="flex items-center justify-center gap-1">
                  <DollarSign className="w-3 h-3" /> Mo Closed: ${(ms.closedRx || 0).toLocaleString()}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-300 absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          );
        })}
      </div>

      {/* Add member — admin only */}
      {isAdmin && (
        <>
          <h3 className="text-[10px] font-bold text-zinc-400 tracking-[0.2em] uppercase mb-3">Add Member</h3>
          <div className="flex gap-2 max-w-sm">
            <input
              data-testid="new-member-input"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleAdd()}
              placeholder="Full name..."
              className="flex-1 px-3 py-2.5 text-sm font-body border border-zinc-200 rounded-md bg-white text-zinc-950 focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 outline-none"
            />
            <button
              data-testid="add-member-btn"
              onClick={handleAdd}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-60"
            >
              <UserPlus className="w-4 h-4" /> Add
            </button>
          </div>
        </>
      )}
    </div>
  );
}
